import {
  MiniKit,
  VerificationLevel,
  type VerifyCommandInput,
} from '@worldcoin/minikit-js';
import { useCallback } from 'react';
import { useShallow } from 'zustand/shallow';
import { CONSTANTS } from '@/constants';
import { useWalletAddress } from '@/hooks/useWalletAddress';
import { useVerifyStore } from '@/stores/use-verify';
import { getMinikitErrorMessage, mergeAPIError } from '@/utils/errors';
import { toaster } from '../toaster';
import { request } from '@/utils/request';

const verifyPayload: VerifyCommandInput = {
  action: 'smash',
  signal: '',
  verification_level:
    (process.env.VITE_VERIFICATION_LEVEL as VerificationLevel) ||
    VerificationLevel.Orb, // Orb | Device
};

function getUrlAndAction(type: string) {
  if (type === 'extraSmash') {
    return {
      action: 'extraSmash',
      url: `${CONSTANTS.BASE_URL}/api/world-coin-chest-smasher/extra-egg/signature?appId=${CONSTANTS.APP_ID}`,
    };
  }

  return {
    action: 'smash',
    url: `${CONSTANTS.BASE_URL}/api/world-coin-chest-smasher/signature?appId=${CONSTANTS.APP_ID}`,
  };
}

export function useVerify() {
  const address = useWalletAddress();
  const [setVerify, setLoading] = useVerifyStore(
    useShallow((state) => [state.setVerify, state.setLoading]),
  );

  const verify = useCallback(
    async (type: 'extraSmash' | 'smash') => {
      try {
        if (!MiniKit.isInstalled()) {
          throw new Error('Minikit is not install');
        }
        setLoading(true);

        const { finalPayload } = await MiniKit.commandsAsync.verify({
          ...verifyPayload,
          signal: address,
          action: type,
        });

        if (finalPayload.status === 'error') {
          setLoading(false);
          const msg = getMinikitErrorMessage(finalPayload.error_code);
          throw new Error(msg);
        }

        const { action, url } = getUrlAndAction(type);
        const body = {
          payload: {
            status: finalPayload.status,
            proof: finalPayload.proof,
            nullifier_hash: finalPayload.nullifier_hash,
            merkle_root: finalPayload.merkle_root,
            verification_level: finalPayload.verification_level,
          },
          action: action,
          signal: address,
        };
        const response = await request(url, {
          method: 'POST',
          body: JSON.stringify(body),
        });

        if (!response.ok) {
          throw new Error(`Error with status: ${response.status}`);
        }

        const real = await response.json();
        if (!real?.success) {
          throw new Error(real?.message);
        }

        if (type === 'extraSmash') {
          setVerify({
            verification_level: finalPayload.verification_level,
            signature: real.data.signature,
            requestId: real.data.refHash,
          });
        } else {
          setVerify({
            verification_level: finalPayload.verification_level,
            signature: real.data.signature,
            requestId: real.data.refHash,
          });
        }
        return true;
      } catch (error) {
        console.log('error', error);
        const message = mergeAPIError(error);
        toaster.error(message);
      } finally {
        setLoading(false);
      }
    },
    [address, setLoading, setVerify],
  );

  return { verify };
}
